/**
 * Recipe ingredient items — the *snapshotted* form of a catalog entry.
 *
 * PLAN §6: "user recipes snapshot the ingredient values at time of use
 * (recipes must not drift when the catalog updates)". These schemas are that
 * snapshot. Each item carries
 *
 * - every numeric field `@beerwright/core` needs to calculate with, copied from
 *   the catalog entry at add-time (see `../bridge/snapshot.ts`);
 * - a {@link CatalogRef} recording *which* entry and *which catalog version*
 *   the numbers came from — provenance only, never dereferenced by the maths;
 * - per-item overrides for the values that legitimately vary batch to batch
 *   (this lot's alpha acid, this sack's actual yield, this pitch's
 *   attenuation).
 *
 * An item with `catalogRef: null` is a fully custom ingredient the user typed
 * in. It calculates identically; it simply has no upstream to compare against.
 * If it also carries an `ingredientId` it came from the brewer's own library,
 * and the id is how the cupboard finds its lots — still a snapshot, still no
 * upstream the maths reads. An item never has both; each schema below is a
 * plain `…FieldsSchema` (what the change sets `.omit()` on) plus the refined
 * export that enforces it.
 */

import { MAX_SUCROSE_PPG } from '@beerwright/core';
import { z } from 'zod';
import {
  acidIdSchema,
  catalogRefSchema,
  decoctionThicknessSchema,
  documentIdSchema,
  fermentableClassSchema,
  fermentableUseSchema,
  fermentationStepTypeSchema,
  fractionSchema,
  hasOneProvenance,
  hopFormSchema,
  hopUseSchema,
  ingredientLinkSchema,
  isoDateSchema,
  maltPhClassSchema,
  mashStepTypeSchema,
  miscAmountUnitSchema,
  miscTypeSchema,
  miscUseSchema,
  nameSchema,
  nonNegativeSchema,
  ONE_PROVENANCE_ISSUE,
  percentSchema,
  positiveSchema,
  saltIdSchema,
  waterTreatmentTargetSchema,
  yeastFormSchema,
  yeastTypeSchema,
} from './common';

// ---------------------------------------------------------------------------
// Fermentables
// ---------------------------------------------------------------------------

/**
 * One fermentable addition in a recipe document, before the one-provenance
 * check — the shape the change-set schema is derived from.
 *
 * Kept as a plain object because `recipeFermentableChangesSchema` calls
 * `.omit().partial()` on it, and Zod refuses both on a refined object. The
 * refined {@link recipeFermentableSchema} below is what documents and `add`
 * payloads parse with; the split is the `blendDocFieldsSchema` / `blendDocSchema`
 * arrangement from `document/blend.ts`, for the same reason. Do not collapse the
 * pair back into one schema: `.omit()` throws the moment a refinement is on it.
 *
 * Maps onto core's `FermentableAddition`. The catalog does not publish PPG (it
 * derives it), so the snapshot carries the two figures PPG is derived *from* —
 * `extractFineGrindDryBasisPercent` and `moisturePercent` — and lets the engine
 * do the arithmetic. `ppgOverride` exists for the brewer who has measured their
 * own yield, and wins in core.
 */
export const recipeFermentableFieldsSchema = z.strictObject({
  /** Stable id, unique within this recipe's fermentable list. */
  itemId: documentIdSchema,
  /** Display name, including the maker: `Great Western Premium 2-Row`. */
  name: nameSchema,
  /** Where the numbers came from, or `null` for a custom fermentable. */
  catalogRef: catalogRefSchema.nullable(),
  /**
   * Which of the brewer's own ingredients this row is, or `null`.
   *
   * Identity, not a live binding: the row keeps the name and the numbers it was
   * created with, exactly as a catalog-backed row does, and a rename in the
   * library does not reach it (D-IL2). What the id buys is matching — the
   * cupboard finds this row's lots by it rather than by a name that, for a
   * custom malt with a maker, never agreed (`"Weyermann Pilsner"` here, bare
   * `"Pilsner"` on the sack). At most one of this and `catalogRef` is set; see
   * {@link hasOneProvenance}. An id that no longer resolves reads as `null`
   * everywhere it is consumed (D-IL8).
   */
  ingredientId: ingredientLinkSchema,
  /** Amount, **kilograms**. */
  amountKg: nonNegativeSchema,
  /** Behaviour class — drives which efficiency core applies. */
  class: fermentableClassSchema,
  /** Point in the process it is added at. */
  use: fermentableUseSchema,
  /** Fine-grind dry-basis extract, **% of dry mass**. */
  extractFineGrindDryBasisPercent: percentSchema,
  /** Moisture as shipped, **% of as-is mass**. */
  moisturePercent: percentSchema,
  /** Colour, **degrees Lovibond**. */
  colorLovibond: z.number().finite().min(0).max(700),
  /** Diastatic power, **°Lintner**. Carried for UI/validation; unused by the vitals maths. */
  diastaticPowerLintner: z.number().finite().min(0).max(300),
  /** Which titration curve the mash-pH model uses (PLAN §5.4). */
  maltClass: maltPhClassSchema,
  /**
   * Measured distilled-water mash pH, where the maltster publishes one.
   *
   * `null` means "use the {@link maltClass} default from core". Never guessed:
   * a wrong DI pH silently poisons the whole pH model.
   */
  distilledWaterPh: z.number().finite().min(3).max(7).nullable(),
  /** Measured buffering capacity, **mEq/kg/pH**, or `null` for the class default. */
  bufferingCapacityMEqPerKgPerPh: z.number().finite().min(-800).max(800).nullable(),
  /** Acidulated malt's lactic acid content, **% by mass**, or `null` for the model default. */
  acidContentPercentLactic: percentSchema.nullable(),
  // --- per-item overrides --------------------------------------------------
  /** Override: measured yield in **PPG**. Wins over the extract/moisture pair in core. */
  ppgOverride: z.number().finite().min(0).max(50).nullable(),
  /** Override: fine/coarse difference, **percentage points** off FGDB. */
  fineCoarseDifferencePercent: z.number().finite().min(0).max(20).nullable(),
  /** Override: **% of this fermentable's extract the yeast consumes** (0–100). */
  fermentabilityPercentOverride: percentSchema.nullable(),
  /** Brewer's note on this addition (lot number, supplier, substitution rationale). */
  note: z.string().max(500).nullable(),
});

/** One fermentable addition, with the one-provenance check. @see recipeFermentableFieldsSchema */
export const recipeFermentableSchema = recipeFermentableFieldsSchema.refine(
  hasOneProvenance,
  ONE_PROVENANCE_ISSUE,
);

/**
 * The yield fields that reproduce a given as-is PPG on the document's scale.
 *
 * Core derives `PPG = ((FGDB − fine/coarse) / 100) × (1 − moisture / 100) ×
 * {@link MAX_SUCROSE_PPG}`. This inverts it: given a PPG and the row's other
 * two figures, what extract percentage makes core land back on that number?
 *
 * ```
 * FGDB = ppg × 100 / (46.214 × (1 − moisture/100)) + fine/coarse
 * ```
 *
 * The constant is imported from core rather than restated here, which is the
 * one place this module reaches across for a runtime value. The reason is that
 * correctness *is* agreement: this function's whole contract is that core's
 * derivation lands back on `ppg`, so it has to invert the arithmetic core will
 * actually perform. A local copy would be the third copy of a physical constant
 * that already disagreed in the fourth decimal once.
 *
 * ## When the ceiling is in the way
 *
 * The document caps FGDB at 100 %, and a near-sucrose sugar overflows it: 46
 * PPG at the 3 % moisture a catalog entry for brown sugar carries would need
 * 102.6 %. Clamping the extract there would quietly cost the row a point of
 * yield, so the *moisture* gives way instead — pin the extract at 100 % and
 * solve for the moisture that reproduces the figure, which is the honest
 * reading: a sugar that gives 46 points as-is is drier than the entry says.
 *
 * Only a figure above what bone-dry sucrose can give is actually unreachable.
 * That comes back `clamped`, at the ceiling, for the caller to announce.
 *
 * @param ppg - The as-is yield to reproduce, points per pound per US gallon.
 * @param row - The row's moisture (**%**) and fine/coarse difference
 *   (**percentage points**, `null` for none) — the two figures core will apply.
 */
export function extractFieldsForPpg(
  ppg: number,
  row: {
    readonly moisturePercent: number;
    readonly fineCoarseDifferencePercent: number | null;
  },
): {
  readonly extractFineGrindDryBasisPercent: number;
  readonly moisturePercent: number;
  /** `true` when `ppg` is beyond anything the two fields can express. */
  readonly clamped: boolean;
} {
  const fineCoarse = row.fineCoarseDifferencePercent ?? 0;
  const dryFraction = 1 - row.moisturePercent / 100;
  const wanted =
    dryFraction > 0
      ? (ppg * 100) / (MAX_SUCROSE_PPG * dryFraction) + fineCoarse
      : Number.POSITIVE_INFINITY;
  if (wanted <= 100) {
    return {
      extractFineGrindDryBasisPercent: storedPercent(wanted),
      moisturePercent: row.moisturePercent,
      clamped: false,
    };
  }
  const dryYield = (ppg * 100) / (MAX_SUCROSE_PPG * (100 - fineCoarse));
  if (dryYield > 1) {
    return { extractFineGrindDryBasisPercent: 100, moisturePercent: 0, clamped: true };
  }
  return {
    extractFineGrindDryBasisPercent: 100,
    moisturePercent: storedPercent((1 - dryYield) * 100),
    clamped: false,
  };
}

/**
 * A derived percentage, at the precision a document is willing to carry.
 *
 * Nine decimals, because that is what BeerJSON's exporter writes a measure at
 * (`interchange/beerjson/units.ts`): a stored figure with more digits than the
 * export format can express does not survive a round trip, and "export then
 * import is the identity" is a property that package tests for. Nine decimal
 * places of a percent is 5 × 10⁻¹² PPG.
 */
function storedPercent(value: number): number {
  return Math.round(value * 1e9) / 1e9;
}

/**
 * Lift one fermentable row off the scale the importer used to write.
 *
 * ## What went wrong, and why this is the fix
 *
 * `ppgOverride` means one thing on this document: *the brewer measured this
 * sack*. `bridge/replace.ts` clears it on a swap for exactly that reason. But
 * until recipe schema 3 the BeerXML/BeerJSON importer used it as a *unit
 * conversion* — somewhere to put a file's `YIELD` without converting it — so a
 * workspace holding both imported and hand-built recipes held two extract
 * scales at once. Nothing computed wrongly from that; a recipe's own PPG was
 * right either way. What it broke was aggregation: `bridge/actuals.ts` takes a
 * median of measured efficiencies across batches, and the two scales made that
 * a median across two different definitions of the grain bill's maximum
 * extract.
 *
 * So the lift closes it at the source, without touching the median. Every
 * non-null `ppgOverride` written under schema 2 came from the importer — no
 * editor for the field existed and the assistant cannot see it — so converting
 * them is not a guess.
 *
 * **The row's PPG does not move.** {@link extractFieldsForPpg} inverts core's
 * own derivation, so `fermentablePpg` returns the same number after the lift
 * that the override returned before it. A brewer's predicted gravity is
 * identical across the migration.
 *
 * A row whose override is beyond anything the extract/moisture pair can express
 * is not reachable in practice — the schema capped the override at 50 PPG and
 * the importer clamped there — but it lands at the ceiling rather than
 * throwing, because a migration that refuses a document is a workspace that
 * will not open.
 *
 * Called only from {@link migrateRecipeExtractScale}, which is what decides
 * *whether* a row is on the old scale. That decision belongs to the document,
 * not to the row: from schema 3 on, a non-null `ppgOverride` is a yield the
 * brewer typed, and lifting it would delete their measurement.
 */
export function liftFermentableYield(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = raw as Record<string, unknown>;
  const override = rec.ppgOverride;
  if (typeof override !== 'number' || !Number.isFinite(override) || override <= 0) return raw;
  const lifted = extractFieldsForPpg(override, {
    moisturePercent: typeof rec.moisturePercent === 'number' ? rec.moisturePercent : 0,
    fineCoarseDifferencePercent:
      typeof rec.fineCoarseDifferencePercent === 'number' ? rec.fineCoarseDifferencePercent : null,
  });
  return {
    ...rec,
    extractFineGrindDryBasisPercent: lifted.extractFineGrindDryBasisPercent,
    moisturePercent: lifted.moisturePercent,
    ppgOverride: null,
  };
}

/** @see recipeFermentableSchema */
export type RecipeFermentable = z.infer<typeof recipeFermentableSchema>;

// ---------------------------------------------------------------------------
// Hops
// ---------------------------------------------------------------------------

/**
 * One hop addition in a recipe document, before the one-provenance check.
 *
 * Plain rather than refined so `recipeHopChangesSchema` can `.omit()` on it —
 * the same split as {@link recipeFermentableFieldsSchema}, for the same reason.
 * The refined {@link recipeHopSchema} below is what documents parse with.
 *
 * The catalog publishes alpha acid as a *range* because it moves with crop
 * year; the snapshot stores a single {@link alphaAcidPercent} (the range's
 * mid-point at add-time) and that field **is** the per-lot override — the
 * brewer types the number off the bag and nothing else has to change.
 */
export const recipeHopFieldsSchema = z.strictObject({
  /** Stable id, unique within this recipe's hop list. */
  itemId: documentIdSchema,
  /** Variety name, e.g. `Citra`. */
  name: nameSchema,
  /** Where the numbers came from, or `null` for a custom hop. */
  catalogRef: catalogRefSchema.nullable(),
  /** Which of the brewer's own ingredients this row is, or `null`. @see recipeFermentableFieldsSchema */
  ingredientId: ingredientLinkSchema,
  /** Amount, **kilograms** (a 28 g addition is `0.028`). */
  amountKg: nonNegativeSchema,
  /**
   * Alpha acid, **% w/w** — this lot's figure. Drives the whole IBU number.
   *
   * Bounded at 100 rather than at the ~30 a cone reaches, because a CO₂
   * extract runs 50-65 % and a recipe may hold one. The cone ceiling belongs
   * to the catalog's own tests, where it can be applied to cones only.
   */
  alphaAcidPercent: z.number().finite().min(0).max(100),
  /** Physical form. Drives the utilisation multiplier. */
  form: hopFormSchema,
  /** Where it goes. */
  use: hopUseSchema,
  /**
   * **Minutes**: boil time remaining for `boil`, stand duration for
   * `whirlpool`; ignored for `first-wort`, `dry-hop` and `keg`.
   */
  timeMinutes: z.number().finite().min(0).max(1440),
  /** Dry-hop contact time, **days**, or `null`. Informational: dry hops are 0 IBU. */
  dryHopDays: z.number().finite().min(0).max(120).nullable(),
  /** Whirlpool temperature at the start of the stand, **°C**, or `null` for core's default. */
  whirlpoolTempC: z.number().finite().min(0).max(100).nullable(),
  /**
   * Wort temperature when the stand **ends**, °C, or `null`.
   *
   * The one number that says how the stand cooled (PLAN-WHIRLPOOL D-WP2): equal
   * to {@link whirlpoolTempC} is a stand held there, below it is a kettle that
   * cooled that far, and `null` is core's own default curve. Bounded like the
   * start rather than against it, because a brewer typing `7` on the way to
   * `70` passes through a value no comparison would allow, and a document that
   * refused it would refuse the keystroke.
   *
   * `.default(null)` so a recipe written before this field existed still
   * parses, which is what keeps `RECIPE_SCHEMA_VERSION` still. See
   * `recipeHopChangesSchema` for the half of that the update patch has to undo.
   */
  whirlpoolEndTempC: z.number().finite().min(0).max(100).nullable().default(null),
  /** Beta acid, **% w/w**. Carried for the UI. Bounded like alpha, and why. */
  betaAcidPercent: z.number().finite().min(0).max(100).nullable(),
  /** Total oil, **mL/100 g**. Carried for the UI. */
  totalOilMlPer100g: z.number().finite().min(0).max(6).nullable(),
  /**
   * Iso-alpha-acid content, **% w/w**, or `null`.
   *
   * Only meaningful when `form` is `iso-extract`, and the reason it is not
   * folded into `alphaAcidPercent` is that the two are different quantities:
   * alpha acid is what isomerises in a kettle, iso-alpha is what already has.
   * Reading one as the other is how a beer comes out a quarter as bitter as
   * intended, so they get separate fields. See `core/ibu.ts`.
   *
   * Optional as well as nullable, and that is a migration decision rather than
   * a stylistic one: every recipe already in a command log or an IndexedDB
   * snapshot was written before this field existed, and a required key would
   * fail them all on parse. Absent and `null` mean the same thing here — no
   * iso-alpha figure — so nothing has to tell them apart.
   */
  isoAlphaPercent: z.number().finite().min(0).max(100).nullable().optional(),
  /** Brewer's note on this addition. */
  note: z.string().max(500).nullable(),
});

/** One hop addition, with the one-provenance check. @see recipeHopFieldsSchema */
export const recipeHopSchema = recipeHopFieldsSchema.refine(hasOneProvenance, ONE_PROVENANCE_ISSUE);

/** @see recipeHopSchema */
export type RecipeHop = z.infer<typeof recipeHopSchema>;

// ---------------------------------------------------------------------------
// Yeast
// ---------------------------------------------------------------------------

/**
 * One yeast selection in a recipe document, before the one-provenance check.
 *
 * Plain rather than refined so `recipeYeastChangesSchema` can `.omit()` on it —
 * the same split as {@link recipeFermentableFieldsSchema}, for the same reason.
 * The refined {@link recipeYeastSchema} below is what documents parse with.
 *
 * Both ends of the published attenuation band are snapshotted so the editor can
 * say "attenuates 78–82 %, predicted FG uses 80 %" (PLAN §4.2) without a
 * catalog lookup. {@link attenuationPercentOverride} is the brewer pinning the
 * figure they actually get.
 */
/**
 * The starter a culture is pitched from, without the `.default(null)` the
 * document row carries.
 *
 * Restated for the update patch, exactly as `FERMENTATION_STEP_PRESSURE_FIELD`
 * is: `.partial()` does not strip a `.default()`, so the defaulted key would
 * materialise on every patch that omits it and an edit to a yeast's pack count
 * would throw away the brewer's starter.
 *
 * ## The inputs, never the yield
 *
 * The step volumes and how the flask is kept moving, and nothing else. The cell
 * count is recomputed from them by the same growth model the starter dialog
 * runs, because storing "220 bn cells" would launder a figure the model's own
 * author puts at ±15 % into a fact this document asserts. It is the reason the
 * row carries a manufacture date and not a viability percentage, applied to the
 * flask instead of the pack.
 *
 * Recomputing is also what keeps the pitch check honest: a two-litre starter in
 * front of a lager can still be too small, and the check goes on saying so with
 * the shortfall it has left.
 *
 * One or two steps. Three is a laboratory propagation schedule and core's
 * planner refuses to produce one, so the document has nowhere to put it. Volumes
 * are bounded like every other numeric field here: the floor is the planner's
 * own quarter-litre increment, below which nothing is being cultured, and the
 * ceiling is well past the 4 L largest step core will plan, because a brewer who
 * owns a 5 L flask and typed it in is not making a mistake.
 *
 * A starter on a **dry** culture is not refused here. It is ignored by core's
 * `pitchPlan`, with a note saying why, because a schema that rejected the
 * combination would turn switching a row from liquid to dry into a document that
 * no longer loads.
 */
export const RECIPE_YEAST_STARTER_FIELD = z
  .strictObject({
    /** Step volumes in order, **litres**. */
    stepVolumesL: z.array(z.number().finite().min(0.25).max(10)).min(1).max(2),
    /** Agitation, which decides which of the growth model's bands applies. */
    method: z.enum(['stir-plate', 'shaken', 'still']),
  })
  .nullable();

export const recipeYeastFieldsSchema = z.strictObject({
  /** Stable id, unique within this recipe's yeast list. */
  itemId: documentIdSchema,
  /** Display name, e.g. `Fermentis SafAle US-05`. */
  name: nameSchema,
  /** Where the numbers came from, or `null` for a custom culture. */
  catalogRef: catalogRefSchema.nullable(),
  /** Which of the brewer's own ingredients this row is, or `null`. @see recipeFermentableFieldsSchema */
  ingredientId: ingredientLinkSchema,
  /** Family. Drives core's default pitch rate. */
  type: yeastTypeSchema,
  /** Packaging form. Drives the viability decay model. */
  form: yeastFormSchema,
  /** Published minimum apparent attenuation, **%**. */
  attenuationMinPercent: percentSchema,
  /** Published maximum apparent attenuation, **%**. */
  attenuationMaxPercent: percentSchema,
  /** Override: the attenuation actually used, **%**. `null` uses the band's mid-point. */
  attenuationPercentOverride: percentSchema.nullable(),
  /** Number of packs/vials/sachets pitched. */
  packs: z.number().finite().min(0).max(100),
  /** Viable cells per pack at manufacture, **billion**, or `null`. */
  cellsPerPackBillion: z.number().finite().min(0).max(1000).nullable(),
  /** Grams per dry sachet, or `null`. */
  gramsPerPack: z.number().finite().min(0).max(5000).nullable(),
  /** Viable cells per gram at manufacture, **billion** (dry yeast), or `null`. */
  cellsPerGramBillion: z.number().finite().min(0).max(100).nullable(),
  /**
   * The maker's recommended dosage, **g/hL**, for a dry strain, or `null`.
   *
   * Optional as well as nullable for the reason `isoAlphaPercent` is: recipes
   * written before this field existed must still parse.
   */
  dosageGramsPerHl: z
    .object({ min: z.number().finite().min(0).max(500), max: z.number().finite().min(0).max(500) })
    .nullable()
    .optional(),
  /** Manufacture date as `YYYY-MM-DD`, for viability decay. Stored as a string, not a `Date`. */
  manufactureDate: isoDateSchema.nullable(),
  /** Published minimum fermentation temperature, **°C**. Carried for the UI. */
  temperatureMinC: z.number().finite().min(-10).max(60).nullable(),
  /** Published maximum fermentation temperature, **°C**. Carried for the UI. */
  temperatureMaxC: z.number().finite().min(-10).max(60).nullable(),
  /**
   * The starter built for this culture, or `null` when the packs go straight
   * into the wort.
   *
   * `.default(null)` so a recipe written before this field existed still parses,
   * which is what keeps `RECIPE_SCHEMA_VERSION` from moving; after parse the key
   * is always present (`T | null`, never absent). See
   * {@link RECIPE_YEAST_STARTER_FIELD} for the half of that the update patch
   * needs instead, and for what is deliberately not stored here.
   */
  starter: RECIPE_YEAST_STARTER_FIELD.default(null),
  /** Brewer's note on this pitch (starter details, harvest generation). */
  note: z.string().max(500).nullable(),
});

/** One yeast selection, with the one-provenance check. @see recipeYeastFieldsSchema */
export const recipeYeastSchema = recipeYeastFieldsSchema.refine(
  hasOneProvenance,
  ONE_PROVENANCE_ISSUE,
);

/** @see recipeYeastSchema */
export type RecipeYeast = z.infer<typeof recipeYeastSchema>;

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/**
 * One miscellaneous addition — finings, nutrients, spices, water treatment —
 * before the one-provenance check.
 *
 * Plain rather than refined so `recipeMiscChangesSchema` can `.omit()` on it —
 * the same split as {@link recipeFermentableFieldsSchema}, for the same reason.
 * The refined {@link recipeMiscSchema} below is what documents parse with.
 *
 * **Miscs do not feed `calculateRecipe`.** Core models no misc concept: salts
 * and acids reach the maths through the recipe's water section
 * (`RecipeWaterInput`), which is typed and solvable, while Whirlfloc and yeast
 * nutrient have no effect on any vital. Keeping them here is a record-keeping
 * and brew-sheet concern, and `recipe.scale` scales their amounts like any
 * other ingredient.
 */
export const recipeMiscFieldsSchema = z.strictObject({
  /** Stable id, unique within this recipe's misc list. */
  itemId: documentIdSchema,
  /** Display name, e.g. `Whirlfloc`. */
  name: nameSchema,
  /** Where it came from, or `null` for a custom addition. */
  catalogRef: catalogRefSchema.nullable(),
  /** Which of the brewer's own ingredients this row is, or `null`. @see recipeFermentableFieldsSchema */
  ingredientId: ingredientLinkSchema,
  /** What kind of thing it is. */
  type: miscTypeSchema,
  /** The stage it is added at. */
  use: miscUseSchema,
  /** Amount, in {@link unit}. */
  amount: nonNegativeSchema,
  /** How the amount is measured. */
  unit: miscAmountUnitSchema,
  /** Contact time at {@link use}, **minutes**, or `null` when timing is meaningless. */
  timeMinutes: z.number().finite().min(0).max(20160).nullable(),
  /** Solution strength, **% w/w** (88 for standard lactic), or `null` for solids. */
  concentrationPercent: z.number().finite().min(0.1).max(100).nullable(),
  /** Brewer's note on this addition. */
  note: z.string().max(500).nullable(),
});

/** One miscellaneous addition, with the one-provenance check. @see recipeMiscFieldsSchema */
export const recipeMiscSchema = recipeMiscFieldsSchema.refine(
  hasOneProvenance,
  ONE_PROVENANCE_ISSUE,
);

/** @see recipeMiscSchema */
export type RecipeMisc = z.infer<typeof recipeMiscSchema>;

// ---------------------------------------------------------------------------
// Water additions
// ---------------------------------------------------------------------------

/** One brewing-salt addition. Maps onto core's `SaltAddition`. */
export const recipeSaltAdditionSchema = z.strictObject({
  /** Stable id, unique within the recipe's salt list. */
  additionId: documentIdSchema,
  /** Which salt. */
  salt: saltIdSchema,
  /** Amount, **grams**. Salts are weighed, never measured by volume. */
  grams: nonNegativeSchema,
  /** Which water it goes into. */
  target: waterTreatmentTargetSchema,
  /**
   * Fraction of the dose that actually dissolves, 0–1, or `null` for core's
   * default of 1. The escape hatch for chalk.
   */
  dissolvedFraction: fractionSchema.nullable(),
});

/** @see recipeSaltAdditionSchema */
export type RecipeSaltAddition = z.infer<typeof recipeSaltAdditionSchema>;

/**
 * One acid addition. Maps onto core's `AcidAddition`.
 *
 * Exactly one of {@link millilitres} (a liquid acid at
 * {@link concentrationPercent}) or {@link grams} (a solid, e.g. citric
 * crystals) must be set — core reports an error rather than guessing when both
 * are present, so the schema refuses the ambiguity up front.
 */
export const recipeAcidAdditionSchema = z
  .strictObject({
    /** Stable id, unique within the recipe's acid list. */
    additionId: documentIdSchema,
    /** Which acid. */
    acid: acidIdSchema,
    /** Volume of acid solution, **millilitres**, or `null` for a solid. */
    millilitres: nonNegativeSchema.nullable(),
    /** Mass of solid acid, **grams**, or `null` for a liquid. */
    grams: nonNegativeSchema.nullable(),
    /** Strength, **% w/w**, or `null` for the acid's default (88 % lactic, 85 % phosphoric). */
    concentrationPercent: z.number().finite().min(0.1).max(100).nullable(),
    /** Which water it goes into. */
    target: waterTreatmentTargetSchema,
  })
  .refine((a) => (a.millilitres === null) !== (a.grams === null), {
    message: 'set exactly one of milliliters (liquid acid) or grams (solid acid)',
  });

/** @see recipeAcidAdditionSchema */
export type RecipeAcidAddition = z.infer<typeof recipeAcidAdditionSchema>;

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------

/**
 * How a decoction step is pulled. Maps onto core's `DecoctionSpec`.
 *
 * Recipe intent rather than rig capability — two brewers on the same kit make
 * different beer by pulling thick rather than thin — so it rides on the step,
 * while the pot, the burner and the tun's standing loss ride on the equipment
 * profile. Every key is present whenever the object is: the *step* says whether
 * it decocts at all by holding `null` here, so there is no second way to mean
 * "unspecified" inside it.
 */
export const recipeDecoctionSchema = z.strictObject({
  /** How thick the pull is. */
  thickness: decoctionThicknessSchema,
  /**
   * Explicit pull thickness, **litres of liquid per kilogram of grain in the
   * pull**, or `null` to take {@link thickness}'s figure. Clamped up to the
   * grain's own absorption by the engine if it asks for something no tun can
   * give.
   */
  ratioLPerKg: positiveSchema.max(20).nullable(),
  /** How long the pull boils, **minutes**. */
  boilMinutes: z.number().finite().min(0).max(240),
  /**
   * A conversion rest held on the pull before it is brought to the boil, or
   * `null` to go straight to the boil.
   *
   * Traditional on a thick pull and not decoration: a pull boiled straight from
   * a protein rest is boiling unconverted starch, which sets it and loses the
   * extract.
   */
  restBeforeBoil: z
    .strictObject({
      tempC: z.number().finite().min(0).max(110),
      minutes: z.number().finite().min(0).max(240),
    })
    .nullable(),
  /**
   * Pull this volume, **litres**, instead of the volume the heat balance asks
   * for — the pot the brewer actually owns. The engine then reports where the
   * mash really lands rather than pretending it hits the target.
   */
  pullVolumeL: positiveSchema.max(1000).nullable(),
});

/** @see recipeDecoctionSchema */
export type RecipeDecoction = z.infer<typeof recipeDecoctionSchema>;

/**
 * The mash step's decoction field, **without** its document default.
 *
 * `recipeMashStepChangesSchema` needs this: `.partial()` does not strip a
 * `.default()`, so the defaulted key would materialise on every patch that
 * omits it and an edit to a rest temperature would wipe the brewer's pull spec.
 * The document needs the default so a recipe saved before decoction existed
 * still parses. Same rule, same shape, as `endTempC` — see
 * `recipeFermentationStepChangesSchema`.
 */
export const MASH_STEP_DECOCTION_FIELD = recipeDecoctionSchema.nullable();

/** One mash step. Maps onto core's `MashStep`, plus a stable id for reordering. */
export const recipeMashStepSchema = z.strictObject({
  /** Stable id, unique within this recipe's mash schedule. */
  stepId: documentIdSchema,
  /** Display name, e.g. `Saccharification`. */
  name: nameSchema,
  /** How the step is reached. */
  type: mashStepTypeSchema,
  /** Rest temperature, **°C**. */
  targetTempC: z.number().finite().min(0).max(110),
  /** Rest duration, **minutes**. */
  timeMinutes: z.number().finite().min(0).max(1440),
  /** Infusion water volume, **litres**, or `null` to let core compute it. */
  infusionVolumeL: nonNegativeSchema.nullable(),
  /** Infusion water temperature, **°C**, or `null` to let core compute it. */
  infusionTempC: z.number().finite().min(0).max(110).nullable(),
  /**
   * Mash thickness for this step, **litres per kilogram of grain**, or `null`
   * to inherit the equipment profile's figure. Only the first step's value is
   * read (it sets the strike-water ratio).
   */
  thicknessLPerKg: positiveSchema.max(20).nullable(),
  /**
   * How a `decoction` step is pulled, or `null` on every other step type.
   *
   * `.default(null)` so a recipe saved before decoction existed still parses,
   * which is what keeps `RECIPE_SCHEMA_VERSION` from moving; see
   * {@link MASH_STEP_DECOCTION_FIELD} for the half of that the update patch
   * needs instead.
   */
  decoction: MASH_STEP_DECOCTION_FIELD.default(null),
});

/** @see recipeMashStepSchema */
export type RecipeMashStep = z.infer<typeof recipeMashStepSchema>;

/**
 * The fermentation step's pressure field, **without** its document default.
 *
 * `recipeFermentationStepChangesSchema` needs this for the reason
 * {@link MASH_STEP_DECOCTION_FIELD} spells out: `.partial()` does not strip a
 * `.default()`, so the defaulted key would materialise on every patch that omits
 * it and an edit to a step's duration would vent the brewer's spund.
 *
 * **PSI, not kPa or bar.** Pressure's canonical unit in this app is psi and is
 * documented as the deliberate odd one out — core's carbonation fit is a
 * polynomial in °F and psi, every published carbonation table is printed in psi,
 * and `UnitPreferences.pressure` already converts to bar and kPa at the display
 * edge. A step storing kPa would be converted on the way into the only function
 * that reads it.
 *
 * Bounded 0–60 psi: a homebrew pressure fermenter is rated somewhere between 15
 * and 35, and 60 is comfortably past anything a brewer means to type while still
 * refusing a stray keyboard.
 */
export const FERMENTATION_STEP_PRESSURE_FIELD = z.number().finite().min(0).max(60).nullable();

/** One fermentation step. Maps onto core's `FermentationStep`, plus a stable id. */
export const recipeFermentationStepSchema = z.strictObject({
  /** Stable id, unique within this recipe's fermentation schedule. */
  stepId: documentIdSchema,
  /** Display name, e.g. `Primary`. */
  name: nameSchema,
  /** Phase. */
  type: fermentationStepTypeSchema,
  /**
   * Hold temperature, **°C**, or the start of a ramp when {@link endTempC} is
   * set. The warmest temperature across the schedule drives residual CO₂ at
   * packaging.
   */
  tempC: z.number().finite().min(-10).max(60),
  /**
   * End of a temperature ramp, **°C**, or `null` for a hold at {@link tempC}.
   *
   * `.default(null)` so a step written before this field existed still parses;
   * after parse the key is always present (`T | null`, never absent).
   */
  endTempC: z.number().finite().min(-10).max(60).nullable().default(null),
  /** Duration, **days**. */
  days: z.number().finite().min(0).max(3650),
  /**
   * Gauge pressure held on the vessel through this step, **psi**, or `null` for
   * a step open to atmosphere — which is every step but a spunded one.
   *
   * `.default(null)` so a step written before this field existed still parses,
   * which is what keeps `RECIPE_SCHEMA_VERSION` from moving; see
   * {@link FERMENTATION_STEP_PRESSURE_FIELD} for the half of that the update
   * patch needs instead.
   *
   * When the **last** step of a schedule carries one, it is what the packaging
   * panel's residual CO₂ is read from, in place of the temperature curve. Core's
   * `scheduleResidualCo2` owns that rule.
   */
  pressurePsi: FERMENTATION_STEP_PRESSURE_FIELD.default(null),
});

/** @see recipeFermentationStepSchema */
export type RecipeFermentationStep = z.infer<typeof recipeFermentationStepSchema>;
